import { Component, Injector, QueryList, ViewChildren } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { Borrower } from '../../../models/borrower-model';
import { ErrorMessage } from '../../../models/error-message.model';
import { MortgageApplicationService } from '../../../services/mortgage-application.service';
import { BorrowerInfoMiniComponent } from '../../borrower-info-mini/borrower-info-mini.component';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { CreateMultipleCoBorrowersStep } from '../../../models/wizard/create-multiple-coborrowers-step.model';
declare const Swal: any;

@Component({
    selector: 'create-multiple-coborrowers',
    templateUrl: 'create-multiple-coborrowers-step.component.html',
    styleUrls: ['create-multiple-coborrowers-step.component.scss'],
    standalone: false
})
export class CreateMultipleCoBorrowersStepComponent extends WizardStepComponentBase<CreateMultipleCoBorrowersStep> {

  @ViewChildren('borrowerInfo') borrowerInfoComponents: QueryList<BorrowerInfoMiniComponent> | undefined;

  coBorrowers: Borrower[] = [];

  borrowerSettings: Map<number, boolean> = new Map<number, boolean>();

  private _invalidBorrowerInfoComponent: BorrowerInfoMiniComponent;

  constructor(private readonly _mortgageApplicationService: MortgageApplicationService,
    private readonly _injector: Injector) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
    this.coBorrowers = this._mortgageApplicationService.getCoBorrowers(this.mortgageApplication);
    if (this.isEditEnabled) {
      const borrower = new Borrower();
      borrower.borrowerId = 12345;
      borrower.firstName = "Jane";
      borrower.lastName = "Spencer";
      borrower.primaryEmail = "jane.spencer@testmail.com";
      borrower.socialSecNum = '777-77-7777';
      borrower.dateOfBirth = '01/01/1977';
      borrower.languagePreference = 'English';
      this.coBorrowers.push(borrower);
    }

    this.borrowerSettings = this.wizardFlowService.context.borrowerSettings;
  }

  protected onBorrowerMarkedAsMarriedToPrimaryBorrower = (borrower: Borrower) => {
    const otherCoBorrowers = this.coBorrowers.filter(b => b.primaryEmail?.toLowerCase() !== borrower.primaryEmail?.toLowerCase());
    otherCoBorrowers.forEach(b => {
      b.isMarriedToPrimaryBorrower = false;
    });
  }

  onNextClicked() {
    this._invalidBorrowerInfoComponent = null;
    let valid = true;
    if (this.borrowerInfoComponents) {
      for (const borrowerinfo of this.borrowerInfoComponents) {
        valid = borrowerinfo.validate() && valid;
        if (!valid && !this._invalidBorrowerInfoComponent) {
          this._invalidBorrowerInfoComponent = borrowerinfo;
        }
      }
    }
    if (valid) {
      if (this.wizardFlowService.isEditEnabled) {
        super.onNextClicked();
        return;
      }

      const borrowersWithDuplicateEmails = this.findPeopleWithDuplicateEmails();
      if (borrowersWithDuplicateEmails.length) {
        this.showDuplicateBorrowersWarning('wizard.coborrowers.uniqueEmailsRequired', borrowersWithDuplicateEmails);
        return;
      }

      // ENG-1977: the API rejects a new co-borrower that shares an SSN with another borrower - catch it here first.
      const borrowersWithDuplicateSsns = this.findPeopleWithDuplicateSsns();
      if (borrowersWithDuplicateSsns.length) {
        this.showDuplicateBorrowersWarning('wizard.coborrowers.uniqueSsnsRequired', borrowersWithDuplicateSsns);
        return;
      }

      this.saveMortgageApplicationBeforeNextStep = false;
      this.startSpinner();

      this.fixBorrowerGroupingsWithinTheApplication();

      const borrowerIdsBeforeSave = new Set(this.mortgageApplication.borrowers.filter(b => b.borrowerId > 0).map(b => b.borrowerId));
      this.wizardFlowService.saveMortgage().subscribe(result => {
        const calls: Observable<any>[] = [];
        try {
          if (this.borrowerInfoComponents) {
            const cards = this.borrowerInfoComponents.map(borrowerinfo => ({
              borrowerinfo,
              insertedBorrower: this.findSavedBorrower(result.borrowers, borrowerinfo.borrower, borrowerIdsBeforeSave),
            }));
            for (const { borrowerinfo, insertedBorrower } of cards.filter(c => c.insertedBorrower)) {
              const isApplyingForBorrower = this.wizardFlowService.context.borrowerSettings.get(borrowerinfo.borrower.borrowerId);
              if (isApplyingForBorrower !== null && isApplyingForBorrower !== undefined) {
                this.wizardFlowService.context.borrowerSettings.delete(borrowerinfo.borrower.borrowerId);
                this.wizardFlowService.context.borrowerSettings.set(insertedBorrower.borrowerId, isApplyingForBorrower);
              }
              // Not persisted by the API, so carry it over to the saved borrower.
              insertedBorrower.isMarriedToPrimaryBorrower = borrowerinfo.borrower.isMarriedToPrimaryBorrower;
              borrowerinfo.setBorrowerIdContactId(insertedBorrower.borrowerId, insertedBorrower.contactId);
            }
            // A new co-borrower missing from the save goes back into the mortgage so a retry sends it again.
            const missing = cards.filter(c => !c.insertedBorrower);
            for (const { borrowerinfo } of missing) {
              if (!(borrowerinfo.borrower.borrowerId > 0) && !this.mortgageApplication.borrowers.includes(borrowerinfo.borrower)) {
                this.mortgageApplication.borrowers.push(borrowerinfo.borrower);
              }
            }
            // The save replaced the mortgage in context; bind the cards to it so edits before a retry are saved.
            this.coBorrowers = this._mortgageApplicationService.getCoBorrowers(this.mortgageApplication);
            if (missing.length) {
              // Surface it instead of leaving the spinner up.
              throw new Error(this.translocoService.translate('wizard.coborrowers.coBorrowerNotSaved',
                { name: this.utilsService.getPersonsFullName(missing[0].borrowerinfo.borrower) }));
            }
            for (const { borrowerinfo, insertedBorrower } of cards) {
              if (!borrowerinfo.mainBorrowerWillApplyForMe) {
                const borrowerInviteToCompleteAppCall = this.mortgageApplicationService.inviteToCompleteAppForCoborrower(this.mortgageApplication.mortgageId,
                  this.mortgageApplication.applicationId, insertedBorrower.borrowerId!);
                calls.push(borrowerInviteToCompleteAppCall);
              } else {
                // Merge borrower needs to be called here for the borrower
                const mergeBorrowerCall = this.mortgageApplicationService.mergeCoborrower(this.mortgageApplication.applicationId,
                  insertedBorrower.contactId!);
                calls.push(mergeBorrowerCall);
              }
            }
            // LLS-886 §5: new co-borrowers are now under their saved ids — record the primary's acknowledgment for them.
            this.wizardFlowService.refreshSpanishAcknowledgmentAppliedFor();
            const coBorrIds = [];
            const nonCoBorrIds = [];
            this.wizardFlowService.context.borrowerSettings.forEach((value, key, map) => {
              if (value) {
                coBorrIds.push(key);
              } else {
                nonCoBorrIds.push(key);
              }
            });
            this.wizardFlowService.context.borrowerAppStatus.details.coBorrowerIds = coBorrIds;
            this.wizardFlowService.context.borrowerAppStatus.details.nonCoBorrowerIds = nonCoBorrIds;
          }
        } catch (error) {
          this.handleError(error);
          return;
        }
        if (calls.length > 0) {
          forkJoin(calls).subscribe(
            (result) => {
              this.stopSpinner();
              super.onNextClicked();
            },
            (error) => {
              this.handleError(error);
            }
          );
        } else {
          super.onNextClicked();
        }
      }, error => {
        this.handleError(error);
      });
    } else {
      this._invalidBorrowerInfoComponent.scrollToFirstInvalid();
    }
  }

  private handleError = (error: any) => {
    this.stopSpinner();
    const errorMessage = new ErrorMessage("An error occurred while saving the mortgage", error?.error?.message ?? error?.error?.detail ?? error?.message);
    this.showError(errorMessage);
    window.scroll(0, 0);
  }

  private findPeopleWithDuplicateEmails = (): Borrower[] => {
    const borrowersWithSameEmails: Borrower[] = [];
    this.mortgageApplication.borrowers.forEach(borrower => {
      // Trimmed and case-insensitive, like the API's duplicate-email handling.
      const peopleWithSameEmail = this.mortgageApplication.borrowers.filter(b => this.normalizeForMatch(b.primaryEmail) ===
        this.normalizeForMatch(borrower.primaryEmail));
      if (peopleWithSameEmail.length > 1) {
        borrowersWithSameEmails.push(borrower);
      }
    });
    return borrowersWithSameEmails;
  }

  /**
   * Mirrors the API rule: a duplicate SSN only blocks when a new borrower (temp id <= 0) is part of it, so loans that
   * already carry duplicate SSNs (ENG-1908) can still move forward.
   */
  private findPeopleWithDuplicateSsns = (): Borrower[] => {
    const ssnDigits = (borrower: Borrower) => (borrower.socialSecNum || '').replace(/\D/g, '');
    const borrowers = this.mortgageApplication.borrowers.filter(b => ssnDigits(b).length === 9);
    return borrowers.filter(borrower => {
      const sameSsn = borrowers.filter(b => ssnDigits(b) === ssnDigits(borrower));
      return sameSsn.length > 1 && sameSsn.some(b => !(b.borrowerId > 0));
    });
  }

  /**
   * An existing borrower is found by id only. A new co-borrower (temp id <= 0) is matched on its trimmed,
   * case-insensitive name and e-mail among the rows this save created; an ambiguous match counts as not found.
   */
  private findSavedBorrower = (savedBorrowers: Borrower[], borrower: Borrower, borrowerIdsBeforeSave: Set<number>): Borrower | undefined => {
    if (borrower.borrowerId > 0) {
      return savedBorrowers.find(b => b.borrowerId === borrower.borrowerId);
    }
    const matches = savedBorrowers.filter(b => !borrowerIdsBeforeSave.has(b.borrowerId)
      && this.normalizeForMatch(b.firstName) === this.normalizeForMatch(borrower.firstName)
      && this.normalizeForMatch(b.lastName) === this.normalizeForMatch(borrower.lastName)
      && this.normalizeForMatch(b.primaryEmail) === this.normalizeForMatch(borrower.primaryEmail));
    return matches.length === 1 ? matches[0] : undefined;
  }

  private normalizeForMatch = (value: string | null | undefined): string => (value || '').trim().toLowerCase();

  private showDuplicateBorrowersWarning = (messageKey: string, borrowers: Borrower[]) => {
    let html = `<div class='mb-4'>${this.translocoService.translate(messageKey)}</div>`;
    this.prepareHtmlForBorrowersWithSameEmails(borrowers).forEach(b => {
      html += b;
    });
    Swal.fire({
      title: this.translocoService.translate('common.warning'),
      html: html,
      icon: 'warning',
      width: '700px',
    });
  }

  private prepareHtmlForBorrowersWithSameEmails = (borrowers: Borrower[]): string[] => {
    const borrowersWithoutEmploymentHtml: string[] = [];
    borrowers.forEach(borrower => {
          borrowersWithoutEmploymentHtml.push(`
          <div class='row mb-2' style='max-width: 350px; margin: 0 auto'>
            <div class='col-12'><strong>${this.utilsService.getPersonsFullName(borrower)}</strong></div>
          <div>`)
        }
    );
    return borrowersWithoutEmploymentHtml;
  }

  private fixBorrowerGroupingsWithinTheApplication = () => {
    let primaryBorrower = this.mortgageApplication.borrowers.find(b => b.isPrimary);
    if (!primaryBorrower) {
      primaryBorrower = this.currentBorrower;
      this.currentBorrower.isPrimary = true;
    }
    if (!primaryBorrower.printApplicationIndex) {
      primaryBorrower.printApplicationIndex = 1;
    }
    let printApplicationIndex = primaryBorrower.printApplicationIndex;
    this.coBorrowers.forEach(coBorrower => {
      const coBorrowerWithinApplication = this.mortgageApplication.borrowers.find(borrower => borrower.primaryEmail === coBorrower.primaryEmail);
      if (coBorrowerWithinApplication) {
        if (this.step.forceCoborrowersIntoSameApplication || coBorrower.isMarriedToPrimaryBorrower) {
          coBorrowerWithinApplication.jointWithBorrowerId = primaryBorrower.borrowerId;
          coBorrowerWithinApplication.printApplicationIndex = primaryBorrower.printApplicationIndex;
        } else {
          printApplicationIndex++;
          coBorrowerWithinApplication.jointWithBorrowerId = null;
          coBorrowerWithinApplication.printApplicationIndex = printApplicationIndex;
        }
      }
    })
  }
}
